import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'

import { IconButton } from '@/components/ui/Button'
import { ToastContext, type ToastApi, type ToastOptions, type ToastTone } from '@/components/ui/toast-context'
import { cn } from '@/lib/cn'

interface Toast {
  id: number
  message: string
  tone: ToastTone
  action?: { label: string; onClick: () => void }
}

const TONES: Record<ToastTone, { className: string; icon: ReactNode }> = {
  info: { className: 'bg-ink text-white', icon: <Info size={18} /> },
  success: { className: 'bg-success text-white', icon: <CheckCircle2 size={18} /> },
  danger: { className: 'bg-danger text-white', icon: <AlertTriangle size={18} /> },
}

/// Замена ScaffoldMessenger: очередь коротких сообщений внизу экрана.
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const id = nextId.current++
      const tone = options.tone ?? 'info'
      // Ошибку читают дольше, чем «Сохранено», — и жалко терять её текст.
      const duration = options.duration ?? (tone === 'danger' ? 6000 : 3500)

      setToasts((current) => [...current, { id, message, tone, action: options.action }])

      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        )
      }
    },
    [dismiss],
  )

  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, options) => show(message, { ...options, tone: 'success' }),
      error: (message, options) => show(message, { ...options, tone: 'danger' }),
    }),
    [show],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex w-full max-w-lg items-center gap-3 rounded-xl px-4 py-3 shadow-pop',
              // Выезжает снизу — оттуда же, где живёт вся очередь сообщений.
              'animate-slide-up',
              TONES[toast.tone].className,
            )}
          >
            <span className="shrink-0">{TONES[toast.tone].icon}</span>
            <p className="min-w-0 flex-1 text-sm whitespace-pre-line">{toast.message}</p>
            {toast.action ? (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick()
                  dismiss(toast.id)
                }}
                className="shrink-0 text-sm font-bold underline underline-offset-2"
              >
                {toast.action.label}
              </button>
            ) : null}
            <IconButton
              label="Скрыть"
              onClick={() => dismiss(toast.id)}
              className="text-white/70 hover:bg-white/15 hover:text-white"
            >
              <X size={16} />
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
